import {ORGANIZATION_TYPES} from '../../utils/constants';

export class OrganizationApiService {
  constructor(organizationType, apiClient) {
    this.organizationType = organizationType;
    this.apiClient = apiClient;
  }

  async fetchResults() {
    switch (this.organizationType) {
      case ORGANIZATION_TYPES.ATPL:
        return this.fetchAtplResults();
      case ORGANIZATION_TYPES.HELLO_HEALTH:
        return this.fetchHelloHealthResults();
      case ORGANIZATION_TYPES.DOKTOR_CONNECT:
        return this.fetchDoktorResults();
      default:
        return this.fetchDefaultResults();
    }
  }

  async fetchAtplResults() {
    const resultsToken = localStorage.getItem('results_token');
    const sessionId = localStorage.getItem('session-id');

    if (!sessionId || !resultsToken) {
      throw new Error('Missing session credentials');
    }

    const response = await this.apiClient.getResults(sessionId);
    return this.transformOrganizationResults(response.data);
  }

  async fetchHelloHealthResults() {
    // HelloHealth specific logic
    return this.fetchAtplResults(); // Similar to ATPL for now
  }

  async fetchDoktorResults() {
    const resultsToken = localStorage.getItem('results_token');
    const sessionId = localStorage.getItem('session-id');
    const isClientResultsUrl = window.location.pathname.includes('client-results');

    if (!sessionId || !resultsToken) {
      throw new Error('Missing session credentials');
    }

    const apiMethod = isClientResultsUrl
      ? this.apiClient.showResultsAfterSingleSessionPayment
      : this.apiClient.getResults;

    const response = await apiMethod(sessionId);
    return this.transformOrganizationResults(response.data);
  }

  async fetchDefaultResults() {
    // Default organization uses different API structure
    const assessmentId = await this.getLatestAssessmentId();
    const resultsApi = new ResultsApiService(this.apiClient);
    return resultsApi.fetchResults(assessmentId);
  }

  transformOrganizationResults(data) {
    if (!data.vitals) {
      throw new Error('Invalid results data');
    }

    const vitals = data.vitals;
    return {
      overallHealth: {
        title: 'Overall Health Score',
        result: vitals.overall_wellness,
      },
      heartHealth: {
        score: vitals.heart_disease_risk,
        bloodPressureSystolic: vitals.blood_pressure_systolic,
        bloodPressureDiastolic: vitals.blood_pressure_diastolic,
        irregularHeartBeatCount: vitals.irregular_heartbeat_count,
        respiration: vitals.respiration,
        pulseRate: vitals.pulse_rate,
      },
      bodyFunction: {
        score: vitals.mental_health_score,
        stressIndex: vitals.stress_index,
        cardiacWorkload: vitals.cardiac_workload,
        heartRateVariability: vitals.heart_rate_change,
        vascularCapacity: vitals.vascular_capacity,
        vitalScore: vitals.vital_score,
      },
      physicalHealth: {
        score: vitals.physiological_health_score || vitals.physical_health_score,
        bodyShapeIndex: vitals.body_shape_index,
        facialSkinAge: vitals.face_skin_age,
        bodyMassIndex: vitals.body_mass_index,
      },
      diseaseRisk: {
        score: vitals.disease_risk_score,
        cardiovascularRisk: vitals.cardiovascular_risk,
        hypertriglyceridemiaRisk: vitals.hypertriglyceridemia_risk,
        heartDiseaseRisk: vitals.heart_disease_risk,
        strokeRisk: vitals.stroke_risk,
      },
      metabolicHealth: {
        diabetesRisk: vitals.diabetes_risk,
        hba1cRisk: vitals.hba1c,
        bloodGlucose: vitals.blood_glucose,
      },
      liverHealth: {
        fattyLiverDiseaseRisk: vitals.fatty_liver_disease_risk,
      },
      showAllResults: true,
    };
  }

  async getLatestAssessmentId() {
    // Implementation to get latest assessment ID
    const assessment = await this.apiClient.getLatestAssessment();
    if (assessment.status === 200) {
      return assessment.data.id;
    }
    throw new Error('Could not get assessment ID');
  }
}
